import React, { useEffect, useMemo, useState } from 'react';
import { Table, Column, AutoSizer, SortDirection } from 'react-virtualized';
import { useSelector } from 'react-redux';
import { Row, Card, Spinner, Button, Col } from 'reactstrap';
import useOpportunities from '../Opportunities/useOpportunities';
import { isMobile, isTablet } from 'react-device-detect';
import MarketingCampaignsFiltersCard from './MarketingCampaignsFiltersCard';
import { Theme } from '../../config/constants';
import AgreementStatusIndicator from '../../sharedComponents/AgreementStatusIndicator/AgreementStatusIndicator';
import { sortableHeaderRenderer } from '../../sharedComponents/Tables/SortableHeaderRenderer';

const MarketingCampaignsStatus = () => {
    const isMobileView = isMobile && !isTablet;
    const isLoading = useSelector(({opportunities}) => opportunities.isLoading);
    const opportunitiesList = useSelector(({opportunities}) => opportunities.opportunitiesItems);
    const opportunitiesFilters = useSelector(({opportunities}) => opportunities.opportunitiesFilterData);
    const selectedFilters = useSelector(({dashboard}) => dashboard.selectedFilters);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState('');
    const [sortDirection, setSortDirection] = useState(null);
    const { handleClickSeeCompInsights, onClickContactAcctManager, getManufacturerAgreements } = useOpportunities.useOpportunities();

    const initializePage = () => {
        getManufacturerAgreements([]);
    };

    // eslint-disable-next-line
    useEffect(initializePage, []);

    const oppsFiltersLength = () => {
        var numberOfFilters = 0;
        if(opportunitiesFilters?.data){
            if(opportunitiesFilters.data.oppSourceStrings?.length > 0) {
                numberOfFilters = numberOfFilters + opportunitiesFilters.data.oppSourceStrings.length;
            }
            if(opportunitiesFilters.data.status?.length > 0) {
                numberOfFilters = numberOfFilters + opportunitiesFilters.data.status.length;
            }
        }
        return numberOfFilters;
    };

    const filterLength = oppsFiltersLength();

    const tableData = useMemo(() => {
        let opportunities = opportunitiesList?.data || [];
        if (selectedFilters?.selectedClientManager?.length > 0) {
            opportunities = opportunities.filter((item) => selectedFilters.selectedClientManager.includes(item.clientmanagername));
        }
        if (selectedFilters?.selectedSource?.length > 0) {
            opportunities = opportunities.filter((item) => selectedFilters.selectedSource.includes(item.complianceactivity));
        }
        if (selectedFilters?.selectedStatus?.length > 0) {
            opportunities = opportunities.filter((item) => selectedFilters.selectedStatus.includes(item.analysisstatus));
        }
        const normalizedSearchTerm = searchTerm.trim().toLowerCase();
        if (normalizedSearchTerm) {
            opportunities = opportunities.filter((item) => {
                const searchableFields = [
                    item?.customer,
                    item?.growthspecialistname,
                    item?.clientmanagername,
                    item?.complianceactivity,
                    item?.analysisstatus,
                    item?.createddate ? new Date(item.createddate).toLocaleDateString('en-US') : ''
                ];
                return searchableFields.some((value) =>
                    String(value || '').toLowerCase().includes(normalizedSearchTerm)
                );
            });
        }

        if (sortBy) {
            const sorted = [...opportunities];
            sorted.sort((a, b) => {
                let aValue = a[sortBy];
                let bValue = b[sortBy];
                const aEmpty = aValue === null || aValue === undefined || aValue === '';
                const bEmpty = bValue === null || bValue === undefined || bValue === '';
                if (aEmpty && !bEmpty) return 1;
                if (!aEmpty && bEmpty) return -1;
                if (aEmpty && bEmpty) return 0;
                if (sortBy === 'createddate') {
                    aValue = aValue ? new Date(aValue).getTime() : 0;
                    bValue = bValue ? new Date(bValue).getTime() : 0;
                }
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
                    return sortDirection === SortDirection.ASC ? comparison : -comparison;
                }
                if (sortDirection === SortDirection.ASC) {
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                } else {
                    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
                }
            });
            return sorted;
        }
        return opportunities;
    }, [opportunitiesList, searchTerm, selectedFilters, sortBy, sortDirection]);

    if (isLoading) {
        return (
            <Row>
                <Col className='mx-3'>
                    <Row>
                        <MarketingCampaignsFiltersCard searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                    </Row>
                    <Row>
                        <div className="mx-auto my-auto" style={{height:"2.5%", width:"2.5%"}}>
                            <Spinner color='beplBlue' animation="border" />
                        </div>
                    </Row>
                </Col>
            </Row>
        );
    }
    if(!isLoading && opportunitiesList?.data?.length === 0 && filterLength === 0) {
        return (
            <Row>
                <Col className="mx-auto mt-5 w-100 h-100">
                    <Row>
                        <MarketingCampaignsFiltersCard searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                    </Row>
                    <Row className="mx-auto my-3 w-fc">
                        <div className="text-beplBlue main-text-header text-size-24" style={{fontWeight: '600'}}>No Opportunities to Display</div>
                    </Row>
                    <Row className="d-flex justify-content-center" style={{height: '40vh'}}>
                        <Col className="col-4">
                            <Card className="h-100">
                                <Row className="w-fc mx-auto mt-5">
                                    <div className="text-beplBlue main-text-header text-size-24" style={{fontWeight: '600', textAlign: 'center'}}>Add Competitive Purchases Opportunities</div>
                                </Row>
                                <Row className="w-fc mx-auto my-auto">
                                    <div className="text-beplBlue main-text-header text-size-16" style={{textAlign: 'center'}}>To add opportunities, view competitive purchases and click learn more.</div>
                                </Row>
                                <Row className="w-fc mx-auto mb-5">
                                    <Button 
                                        block 
                                        className='py-2 button-label' 
                                        size='sm' 
                                        onClick = {() => handleClickSeeCompInsights()}
                                        color={Theme().colors.accentName}>VIEW COMPETITIVE PURCHASES
                                    </Button>
                                </Row>
                            </Card>
                        </Col>
                        <Col className="col-4">
                            <Card className="h-100">
                            <Row className="w-fc mx-auto mt-5">
                                    <div className="text-beplBlue main-text-header text-size-24" style={{fontWeight: '600', textAlign: 'center'}}>Connect with your Account Manager</div>
                                </Row>
                                <Row className="w-fc mx-4 my-auto">
                                    <div className="text-beplBlue main-text-header text-size-16" style={{textAlign: 'center'}}>There are plenty of ways to generate opportunities with BEP operators! Connect with your Account Manager to learn more.</div>
                                </Row>
                                <Row className="w-fc mx-auto mb-5">
                                    <Button 
                                        block 
                                        className='py-2 button-label' 
                                        size='sm' 
                                        onClick={() => onClickContactAcctManager()}
                                        color={Theme().colors.accentName}>EXPLORE OPPORTUNITIES WITH ACCOUNT MANAGER
                                    </Button>
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
        )
    }
    return (
        <Col className='mx-3' style={{height:'100%', display:'flex', flexDirection:'column'}}>
            <Row>
                <MarketingCampaignsFiltersCard searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </Row>
            <Row className='pb-2' style={{overflowY: 'auto', flexGrow: 1}}>
                <Col className="h-100 px-0">
                    <AutoSizer>
                        {({ width, height }) => (
                            <Table
                                className="table opportunitiesTable border-grayLight"
                                id="opportunitiesTable"
                                width={width}
                                height={height}
                                rowHeight={38}
                                headerHeight={34.5}
                                rowCount={tableData?.length}
                                rowGetter={({ index }) => tableData?.[index]}
                                sort={({ sortBy: sb, sortDirection: sd }) => {
                                        setSortBy(sb);
                                        setSortDirection(sd);
                                    }}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                            >
                                <Column
                                    label="CUSTOMER"
                                    dataKey="customer"
                                    width={!isMobileView ? width * 0.2 : width * 0.35}
                                    headerRenderer={sortableHeaderRenderer}
                                />
                                {!isMobileView &&
                                <Column
                                    label="MFR GROWTH SPECIALIST"
                                    dataKey="growthspecialistname"
                                    width={width * 0.18}
                                    headerRenderer={sortableHeaderRenderer}
                                />
                                }
                                {!isMobileView &&
                                <Column
                                    label="CLIENT MANAGER"
                                    dataKey="clientmanagername"
                                    width={width * 0.16}
                                    headerRenderer={sortableHeaderRenderer}
                                />
                                }
                                {!isMobileView &&
                                <Column
                                    label="OPPORTUNITY SOURCE"
                                    dataKey="complianceactivity"
                                    width={width * 0.16}
                                    headerRenderer={sortableHeaderRenderer}
                                />
                                }
                                {!isMobileView &&
                                <Column
                                    label="CREATION DATE"
                                    dataKey="createddate"
                                    width={width * 0.12}
                                    cellRenderer={({ cellData }) => <span>{new Date(cellData).toLocaleDateString('en-US')}</span>}
                                    headerRenderer={sortableHeaderRenderer}
                                />
                                }
                                <Column
                                    label="OPPORTUNITY STATUS"
                                    dataKey="analysisstatus"
                                    width={!isMobileView ? width * 0.18 : width * 0.65}
                                    cellRenderer={({ cellData }) => <AgreementStatusIndicator status={cellData} />}
                                    headerRenderer={sortableHeaderRenderer}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                    <div className="text-size-16 mx-auto" style={{display: (tableData.length === 0 ? 'block' : 'none'), textAlign:'center', color: Theme().colors.accent}}>No opportunities to display.</div>
                </Col>
            </Row>
        </Col>
    );
}

export default MarketingCampaignsStatus;